import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiSend, FiMessageSquare } from "react-icons/fi";
import MemberPage from "../components/cowork/MemberPage";
import Avatar from "../components/cowork/Avatar";
import { apiRequest, errorMessage, timeAgoShort } from "../api";
import { cardClass } from "../components/cowork/jobUtils";

function Chat({ user }) {
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const lastId = useRef(0);
  const endRef = useRef(null);

  const requested = Number(params.get("c")) || null;
  const activeId = requested || convs?.[0]?.id || null;
  const active = convs?.find((c) => c.id === activeId);

  const loadConvs = useCallback(async () => {
    const { ok, data } = await apiRequest("/core/cowork/chats/", { auth: true });
    setConvs(ok ? data : []);
    window.dispatchEvent(new Event("cowork:chats"));   // keeps the sidebar's chat list and unread badge in sync
  }, []);

  useEffect(() => {
    loadConvs();
    const timer = setInterval(loadConvs, 10000);
    return () => clearInterval(timer);
  }, [loadConvs]);

  // Open thread: load it (this marks the other side's messages as read), then poll for new ones.
  useEffect(() => {
    setMessages([]);
    setError("");
    lastId.current = 0;
    if (!activeId) return undefined;
    let alive = true;
    const pull = async () => {
      const { ok, data } = await apiRequest(`/core/cowork/chats/${activeId}/messages/?after=${lastId.current}`, { auth: true });
      if (!alive || !ok || data.length === 0) return;
      lastId.current = data[data.length - 1].id;
      setMessages((prev) => [...prev, ...data.filter((m) => !prev.some((p) => p.id === m.id))]);
      loadConvs();   // the unread badge drops once messages are read
    };
    pull();
    const timer = setInterval(pull, 4000);
    return () => { alive = false; clearInterval(timer); };
  }, [activeId, loadConvs]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, activeId]);

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    setError("");
    const { ok, data } = await apiRequest(`/core/cowork/chats/${activeId}/messages/`, { method: "POST", auth: true, body: { body } });
    setSending(false);
    if (!ok) return setError(errorMessage(data));
    lastId.current = Math.max(lastId.current, data.id);
    setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
    setText("");
    loadConvs();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) send(e);
  };

  return (
    <>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-6">Messages</h1>

      {convs === null ? (
        <p className="text-gray-400">Loading...</p>
      ) : convs.length === 0 ? (
        <div className={`${cardClass} text-center py-16 px-6`}>
          <FiMessageSquare className="mx-auto text-accent-light" size={36} />
          <p className="text-lg font-semibold mt-3">No conversations yet</p>
          <p className="text-gray-400 mt-1 mb-6 max-w-md mx-auto">
            {user.role === "buyer"
              ? "Search for a seller and press Message to start a chat."
              : "Apply to a job, then you can message the buyer from your dashboard."}
          </p>
          <Link to={user.role === "buyer" ? "/co-work?find=1" : "/co-work#jobs"} className="px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">
            {user.role === "buyer" ? "Find Seller" : "Browse Jobs"}
          </Link>
        </div>
      ) : (
        <div className={`${cardClass} grid md:grid-cols-[260px_minmax(0,1fr)] overflow-hidden min-h-[520px]`}>
          <ul className="border-b md:border-b-0 md:border-r border-navy-border max-h-[520px] overflow-y-auto">
            {convs.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => setParams({ c: c.id })}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-navy-border/60 transition-colors ${c.id === activeId ? "bg-accent/15" : "hover:bg-white/5"}`}
                >
                  <Avatar src={c.other.avatar} name={c.other.name} size={40} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${c.unread ? "font-bold" : "font-medium"}`}>{c.other.name}</span>
                      {c.last_message && <span className="text-[10px] text-gray-500 shrink-0">{timeAgoShort(c.last_message.created_at)}</span>}
                    </span>
                    <span className="block text-xs text-gray-400 truncate">{c.last_message ? `${c.last_message.mine ? "You: " : ""}${c.last_message.body}` : "No messages yet"}</span>
                  </span>
                  {c.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-accent-light text-navy-darker text-xs font-bold flex items-center justify-center">{c.unread}</span>}
                </button>
              </li>
            ))}
          </ul>

          <div className="flex flex-col min-h-[520px]">
            {active && (
              <div className="px-5 py-3 border-b border-navy-border flex items-center gap-3">
                <Avatar src={active.other.avatar} name={active.other.name} size={38} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold leading-tight truncate">{active.other.name}</p>
                  <p className="text-xs text-gray-400 truncate capitalize">{active.other.role}{active.other.headline && ` · ${active.other.headline}`}</p>
                </div>
                {active.other.seller_profile_id && (
                  <Link to={`/co-work/sellers/${active.other.seller_profile_id}`} className="text-xs text-accent-light hover:underline shrink-0">View profile</Link>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5 max-h-[420px]">
              {messages.length === 0 && <p className="text-center text-sm text-gray-500 py-10">No messages yet - say hello!</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                    m.mine ? "bg-brand-gradient text-white rounded-br-md" : "bg-white/10 text-gray-100 rounded-bl-md"
                  }`}>
                    {m.body}
                    <span className={`block text-[10px] mt-1 ${m.mine ? "text-white/70" : "text-gray-500"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {m.mine && m.read && " · Seen"}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <form onSubmit={send} className="border-t border-navy-border p-3 flex items-end gap-2">
              <textarea
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Write a message... (Enter to send, Shift+Enter for a new line)"
                className="flex-1 resize-none px-4 py-2.5 rounded-xl bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light max-h-32"
              />
              <button disabled={!text.trim() || sending} className="w-11 h-11 shrink-0 rounded-full bg-brand-gradient flex items-center justify-center disabled:opacity-40" aria-label="Send">
                <FiSend />
              </button>
            </form>
            {error && <p className="px-5 pb-3 text-sm text-red-400">{error}</p>}
          </div>
        </div>
      )}
    </>
  );
}

export default function CoWorkMessagesPage() {
  return <MemberPage title="Messages">{({ user }) => <Chat user={user} />}</MemberPage>;
}
